import { useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import type { Job, Issue } from "@/data/types";
import type { Quote } from "@/data/quoteTypes";
import { isWonStatus } from "@/data/quoteTypes";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";


export interface SalesRepData {
  repName: string;
  myJobs: Job[];
  myQuotes: Quote[];
  myIssues: Issue[];
  myCustomerNames: string[];
  /** Commission snapshot */
  commissions: {
    totalClosed: number;
    estimatedCommission: number;
    paidCommission: number;
    pendingCommission: number;
  };
  /** Today's appointments (jobs with scheduled date = today or upcoming) */
  todaysSchedule: Job[];
  /** Pipeline metrics */
  pipeline: {
    activeQuotes: number;
    pipelineValue: number;
    wonThisMonth: number;
    wonValue: number;
    lostThisMonth: number;
    winRate: number;
  };
}

const COMMISSION_RATE = 0.08; // 8% default

export function useSalesRepData(): SalesRepData {
  const { jobs, quotes, issues } = useAppData();
  const { repName: currentRep } = useCurrentRep();

  return useMemo(() => {
    const repName = currentRep;

    // Filter to rep's data only
    const myJobs = jobs.filter(
      (j) => j.salesRep === repName || j.assignedCrew === repName
    );
    const myQuotes = quotes.filter((q) => q.createdBy === repName);
    const myJobIds = new Set(myJobs.map((j) => j.id));
    const myIssues = issues.filter((i) => myJobIds.has(i.jobId));
    const myCustomerNames = [...new Set([
      ...myJobs.map((j) => j.customerName),
      ...myQuotes.map((q) => q.customerName),
    ])];

    // Commissions
    const completedJobs = myJobs.filter((j) => j.currentStage === "Completed");
    const totalClosed = myJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const completedRevenue = completedJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const estimatedCommission = Math.round(totalClosed * COMMISSION_RATE);
    const paidCommission = Math.round(completedRevenue * COMMISSION_RATE);
    const pendingCommission = estimatedCommission - paidCommission;

    // Today's schedule
    const today = new Date().toISOString().slice(0, 10);
    const todaysSchedule = myJobs
      .filter((j) => {
        if (!j.scheduledInstallDate) return false;
        return j.scheduledInstallDate >= today;
      })
      .sort((a, b) => (a.scheduledInstallDate || "").localeCompare(b.scheduledInstallDate || ""))
      .slice(0, 10);

    // Pipeline
    const activeQuotes = myQuotes.filter((q) => !["Won", "Lost", "Converted"].includes(q.status));
    const pipelineValue = activeQuotes.reduce((s, q) => s + (q.totalEstimate || 0), 0);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const wonThisMonth = myQuotes.filter((q) => (isWonStatus(q.status)) && q.wonAt && q.wonAt >= monthStart);
    const lostThisMonth = myQuotes.filter((q) => q.status === "Closed Lost" && q.lostAt && q.lostAt >= monthStart);
    const totalDecided = wonThisMonth.length + lostThisMonth.length;
    const winRate = totalDecided > 0 ? Math.round((wonThisMonth.length / totalDecided) * 100) : 0;

    return {
      repName,
      myJobs,
      myQuotes,
      myIssues,
      myCustomerNames,
      commissions: {
        totalClosed,
        estimatedCommission,
        paidCommission,
        pendingCommission,
      },
      todaysSchedule,
      pipeline: {
        activeQuotes: activeQuotes.length,
        pipelineValue,
        wonThisMonth: wonThisMonth.length,
        wonValue: wonThisMonth.reduce((s, q) => s + (q.totalEstimate || 0), 0),
        lostThisMonth: lostThisMonth.length,
        winRate,
      },
    };
  }, [jobs, quotes, issues]);
}
